import React from "react";
import i18next from "i18next";
import styles from "./ErrorBoundary.module.css";

type ErrorBoundaryProps = {
  children: React.ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
};

export default class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error(i18next.t("common.errors.errorBoundaryLog"), { error, errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const showRawError = process.env.NODE_ENV !== "production";

    return (
      <main className={styles.shell}>
        <section className={styles.card} role="alert" aria-live="assertive">
          <p className={styles.kicker}>{i18next.t("common.brand")}</p>
          <h1 className={styles.title}>{i18next.t("common.errors.unexpectedErrorTitle")}</h1>
          <p className={styles.text}>
            {i18next.t("common.errors.unexpectedErrorDescription")}
          </p>
          {showRawError && this.state.error && (
            <pre className={styles.errorDetail}>{this.state.error.message}</pre>
          )}
          <button type="button" className={styles.reloadButton} onClick={this.handleReload}>
            {i18next.t("common.actions.reloadPage")}
          </button>
        </section>
      </main>
    );
  }
}
